import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const NatureMetaContext = createContext();

export const useNatureMeta = () => {
  const context = useContext(NatureMetaContext);
  if (!context) {
    throw new Error('useNatureMeta must be used within a NatureMetaProvider');
  }
  return context;
};

// Valid detail-table types — matches the backend's LocationType const.
// Charcos route through 'river' (shared schema). Picos through 'mountain'.
// Caves through 'cavern'. Lagoons use 'lagoon' (URL segment, not 'laguna').
const VALID_DETAIL_TYPES = new Set([
  'beach', 'river', 'waterfall', 'mountain', 'cavern', 'lagoon',
]);

export const NatureMetaProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  // API base URL for nature meta endpoints
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://7d515f15e81f.ngrok-free.app';

  // Determine the correct base URL based on login type
  const getNatureMetaBaseUrl = useCallback(() => {
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/nature-meta/api/v1/nature-meta`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/nature-meta/api/v1/nature-meta`;
    } else {
      throw new Error('Invalid login type for nature meta access');
    }
  }, [API_BASE_URL, loginType]);

  // Helper function to handle API responses.
  // Supports an `allowNotFound` flag for read methods so detail rows that
  // don't exist yet return null instead of throwing.
  const handleApiResponse = async (response, { allowNotFound = false } = {}) => {
    if (response.status === 404 && allowNotFound) {
      return null;
    }

    // DELETE responses sometimes have empty bodies — guard against JSON parse errors.
    let data = null;
    const text = await response.text();
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = { raw: text };
      }
    }

    if (!response.ok) {
      const msg = (data && (data.error || data.message)) || 'Request failed';
      const err = new Error(msg);
      err.status = response.status;
      err.data = data;
      throw err;
    }

    return data;
  };

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Validate user access
  const validateAccess = useCallback(() => {
    if (loginType !== 'admin' && loginType !== 'master') {
      throw new Error('Only admin and master users can access nature meta');
    }
  }, [loginType]);

  // Validate detail type (internal helper)
  const validateDetailType = (type) => {
    if (!VALID_DETAIL_TYPES.has(type)) {
      throw new Error(
        `Invalid detail type "${type}". Allowed: ${[...VALID_DETAIL_TYPES].join(', ')}`
      );
    }
  };

  // =========================================================================
  // LIST  (GET /api/v1/nature-meta?...)
  // =========================================================================

  // Fetch natural locations with optional filters. Used by the admin
  // dashboard's metadata management page.
  const fetchLocations = useCallback(async (options = {}) => {
    validateAccess();

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();

      if (options.type) queryParams.append('type', options.type);
      if (options.city) queryParams.append('city', options.city);
      if (options.search) queryParams.append('search', options.search);
      if (options.has_meta !== undefined) queryParams.append('has_meta', options.has_meta);
      if (options.limit) queryParams.append('limit', options.limit);
      if (options.offset) queryParams.append('offset', options.offset);

      const baseUrl = getNatureMetaBaseUrl();
      const fullUrl = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;

      const response = await makeAuthenticatedRequest(fullUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch locations';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  // =========================================================================
  // FULL META  (combined common + detail in one call)
  // =========================================================================

  // Get the full meta bundle (common + the relevant detail block).
  // Returns null if the location doesn't exist (404 handled gracefully).
  const getFullMeta = useCallback(async (locationId) => {
    validateAccess();

    if (!locationId) {
      throw new Error('Location ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getNatureMetaBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(locationId)}`
      );
      const result = await handleApiResponse(response, { allowNotFound: true });
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch full meta';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  // Atomic upsert: applies common metadata + a single detail block in one
  // transaction. Payload shape:
  //   { common: {...}, beach_details: {...} }   (or river_details, etc.)
  // The detail block must match the location's actual type — backend
  // returns 400 on mismatch.
  const upsertFullMeta = useCallback(async (locationId, payload) => {
    validateAccess();

    if (!locationId) {
      throw new Error('Location ID is required');
    }
    if (!payload || typeof payload !== 'object') {
      throw new Error('Payload is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getNatureMetaBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(locationId)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to save full meta';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  // =========================================================================
  // COMMON METADATA  (prlocal_locations columns)
  // =========================================================================

  const getCommonMeta = useCallback(async (locationId) => {
    validateAccess();

    if (!locationId) {
      throw new Error('Location ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getNatureMetaBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(locationId)}/common`
      );
      const result = await handleApiResponse(response, { allowNotFound: true });
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch common meta';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  const updateCommonMeta = useCallback(async (locationId, payload) => {
    validateAccess();

    if (!locationId) {
      throw new Error('Location ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getNatureMetaBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(locationId)}/common`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload || {}),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to update common meta';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  // NULL out every common metadata field. Does NOT delete the location row.
  const clearCommonMeta = useCallback(async (locationId) => {
    validateAccess();

    if (!locationId) {
      throw new Error('Location ID is required');
    }

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getNatureMetaBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(locationId)}/common`,
        {
          method: 'DELETE',
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to clear common meta';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  // =========================================================================
  // DETAIL TABLES  (per-type)
  // =========================================================================

  const getDetails = useCallback(async (locationId, type) => {
    validateAccess();

    if (!locationId) {
      throw new Error('Location ID is required');
    }
    validateDetailType(type);

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getNatureMetaBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(locationId)}/details/${type}`
      );
      const result = await handleApiResponse(response, { allowNotFound: true });
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch details';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  const upsertDetails = useCallback(async (locationId, type, payload) => {
    validateAccess();

    if (!locationId) {
      throw new Error('Location ID is required');
    }
    validateDetailType(type);

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getNatureMetaBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(locationId)}/details/${type}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload || {}),
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to save details';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  const deleteDetails = useCallback(async (locationId, type) => {
    validateAccess();

    if (!locationId) {
      throw new Error('Location ID is required');
    }
    validateDetailType(type);

    setLoading(true);
    setError(null);

    try {
      const baseUrl = getNatureMetaBaseUrl();
      const response = await makeAuthenticatedRequest(
        `${baseUrl}/${encodeURIComponent(locationId)}/details/${type}`,
        {
          method: 'DELETE',
        }
      );
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to delete details';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, validateAccess, getNatureMetaBaseUrl]);

  // =========================================================================
  // PER-TYPE SUGAR  (delegate to generic methods, keep the API ergonomic)
  // =========================================================================

  const getBeachDetails     = useCallback((id) => getDetails(id, 'beach'),     [getDetails]);
  const getRiverDetails     = useCallback((id) => getDetails(id, 'river'),     [getDetails]);
  const getWaterfallDetails = useCallback((id) => getDetails(id, 'waterfall'), [getDetails]);
  const getMountainDetails  = useCallback((id) => getDetails(id, 'mountain'),  [getDetails]);
  const getCavernDetails    = useCallback((id) => getDetails(id, 'cavern'),    [getDetails]);
  const getLagoonDetails    = useCallback((id) => getDetails(id, 'lagoon'),    [getDetails]);

  const upsertBeachDetails     = useCallback((id, p) => upsertDetails(id, 'beach', p),     [upsertDetails]);
  const upsertRiverDetails     = useCallback((id, p) => upsertDetails(id, 'river', p),     [upsertDetails]);
  const upsertWaterfallDetails = useCallback((id, p) => upsertDetails(id, 'waterfall', p), [upsertDetails]);
  const upsertMountainDetails  = useCallback((id, p) => upsertDetails(id, 'mountain', p),  [upsertDetails]);
  const upsertCavernDetails    = useCallback((id, p) => upsertDetails(id, 'cavern', p),    [upsertDetails]);
  const upsertLagoonDetails    = useCallback((id, p) => upsertDetails(id, 'lagoon', p),    [upsertDetails]);

  const deleteBeachDetails     = useCallback((id) => deleteDetails(id, 'beach'),     [deleteDetails]);
  const deleteRiverDetails     = useCallback((id) => deleteDetails(id, 'river'),     [deleteDetails]);
  const deleteWaterfallDetails = useCallback((id) => deleteDetails(id, 'waterfall'), [deleteDetails]);
  const deleteMountainDetails  = useCallback((id) => deleteDetails(id, 'mountain'),  [deleteDetails]);
  const deleteCavernDetails    = useCallback((id) => deleteDetails(id, 'cavern'),    [deleteDetails]);
  const deleteLagoonDetails    = useCallback((id) => deleteDetails(id, 'lagoon'),    [deleteDetails]);

  // =========================================================================
  // UTILITY
  // =========================================================================

  // Health check
  const checkHealth = useCallback(async () => {
    validateAccess();

    try {
      const healthUrl = loginType === 'master'
        ? `${API_BASE_URL}/master/nature-meta/health`
        : `${API_BASE_URL}/admin/nature-meta/health`;

      const response = await makeAuthenticatedRequest(healthUrl);
      const result = await handleApiResponse(response);
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Health check failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }, [makeAuthenticatedRequest, validateAccess, API_BASE_URL, loginType]);

  // Get endpoint info for debugging
  const getEndpointInfo = useCallback(() => {
    return {
      loginType,
      baseUrl: loginType === 'master'
        ? `${API_BASE_URL}/master/nature-meta/api/v1/nature-meta`
        : `${API_BASE_URL}/admin/nature-meta/api/v1/nature-meta`,
      accessLevel: loginType === 'master' ? 'Master Access' : 'Admin Access',
      requiredHeaders: loginType === 'master'
        ? ['Authorization', 'secret_credential']
        : ['Authorization', 'admin_id'],
    };
  }, [loginType, API_BASE_URL]);

  const value = {
    // State
    loading,
    error,

    // List
    fetchLocations,

    // Full meta (combined common + detail block, atomic)
    getFullMeta,
    upsertFullMeta,

    // Common metadata (prlocal_locations columns)
    getCommonMeta,
    updateCommonMeta,
    clearCommonMeta,

    // Generic detail operations
    getDetails,
    upsertDetails,
    deleteDetails,

    // Per-type sugar — same as above with type baked in
    getBeachDetails, upsertBeachDetails, deleteBeachDetails,
    getRiverDetails, upsertRiverDetails, deleteRiverDetails,
    getWaterfallDetails, upsertWaterfallDetails, deleteWaterfallDetails,
    getMountainDetails, upsertMountainDetails, deleteMountainDetails,
    getCavernDetails, upsertCavernDetails, deleteCavernDetails,
    getLagoonDetails, upsertLagoonDetails, deleteLagoonDetails,

    // Utility
    clearError,
    checkHealth,
    getEndpointInfo,

    // Computed values
    isAdminUser: loginType === 'admin',
    isMasterUser: loginType === 'master',
    hasNatureMetaAccess: loginType === 'admin' || loginType === 'master',
    currentAccessLevel: loginType,
  };

  return (
    <NatureMetaContext.Provider value={value}>
      {children}
    </NatureMetaContext.Provider>
  );
};